import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems = [
    { label: 'ホーム', href: '/', type: 'page' },
    { label: 'ホイール最新ニュース', href: '#news', type: 'anchor' },
    { label: '使い方ガイド', href: '/guide', type: 'page' },
    { label: 'お問い合わせ', href: '/contact', type: 'page' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuClick = (href: string, type: string) => {
    setIsMenuOpen(false);
    
    if (type === 'anchor') {
      // アンカーリンクの場合 - TOPページのセクションへ
      if (window.location.pathname !== '/') {
        // 別ページにいる場合はTOPページに移動してからスクロール
        window.location.href = '/' + href;
      } else {
        // すでにTOPページにいる場合はスクロール
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else if (href === '/') {
      // ホームの場合 - TOPページへ移動
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // 他のページへの遷移
      window.location.href = href;
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled 
            ? 'bg-[#0a0a0a]/90 backdrop-blur-xl shadow-lg' 
            : 'bg-transparent'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-3"
          >
            <img 
              src="https://static.readdy.ai/image/4b210e4d3d9fe9a73b886d10fa116566/34de0253fd17802be692fff692668476.png"
              alt="3DCARS.JP"
              className="w-10 h-10 object-contain"
            />
            <span className="text-white text-lg font-thin tracking-[0.25em] uppercase">
              3DCARS.JP
            </span>
          </motion.div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="メニュー"
          >
            <div className="grid grid-cols-2 gap-1.5">
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ 
                    scale: isMenuOpen ? 0 : 1,
                    rotate: isMenuOpen ? 180 : 0
                  }}
                  className="w-2 h-2 rounded-full bg-[#00d4ff]"
                />
              ))}
            </div>
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed inset-0 z-50 bg-[#0a0a0a]/98 backdrop-blur-2xl"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8 px-5">
              <button
                onClick={() => setIsMenuOpen(false)}
                className="absolute top-6 right-5 text-white text-4xl cursor-pointer"
                aria-label="閉じる"
              >
                ×
              </button>

              {menuItems.map((item, index) => (
                <motion.button
                  key={item.label}
                  onClick={() => handleMenuClick(item.href, item.type)}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-white text-3xl font-bold hover:text-[#00d4ff] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {item.label}
                </motion.button>
              ))}

              <div className="flex gap-6 mt-8">
                <motion.a
                  href="https://www.instagram.com/3dcars_official/"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2 }}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-[#2a2a2a] text-white hover:bg-gradient-to-br hover:from-[#f09433] hover:via-[#e1306c] hover:to-[#c13584] transition-all cursor-pointer"
                >
                  <i className="ri-instagram-fill text-xl"></i>
                </motion.a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
